import type { TypedMoneyBasis } from "../api/types";

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * G-121 S3 — the state rules of the "Before IVA / Includes IVA" switch
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Beside every typed purchase price the person says which side of IVA it is
 * on (owner, 2026-10-01). These are the switch's STATE rules, shared by the
 * five screens that carry one — never the IVA arithmetic, which lives only on
 * the server (`src/common/pricing/typed-money.ts`; see the header of
 * `utils/priceFloor.ts` for why the browser keeps no copy).
 *
 *  - OD-138: an EMPTY box has no side — there is no default, because the
 *    biggest number on a Spanish invoice is the total WITH IVA and any
 *    default would silently be wrong a good part of the time.
 *  - A box the app pre-filled from a stored price starts on Before IVA:
 *    every stored purchase price is before IVA.
 *  - Emptying the box drops its side; the next figure typed is picked anew.
 */

/** The side a box opens on: Before IVA when the app filled it, none when empty. */
export function basisForPrefill(value: string | null | undefined): TypedMoneyBasis | null {
  return value != null && value.trim() !== "" ? "BEFORE_IVA" : null;
}

/** After an edit: an emptied box loses its side; any other edit keeps it. */
export function basisAfterEdit(
  previous: TypedMoneyBasis | null,
  nextValue: string,
): TypedMoneyBasis | null {
  return nextValue.trim() === "" ? null : previous;
}

/** Saving needs a side whenever the box holds an amount. */
export function needsBasis(value: string, basis: TypedMoneyBasis | null): boolean {
  return value.trim() !== "" && basis === null;
}
